import { Injectable } from '@angular/core';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { AsYouType, isValidPhoneNumber } from 'libphonenumber-js';
import { CountryPhone, DIAL_CODE_DEFAULT_COUNTRY } from './phone-input.models';

export interface ParseAsYouTypeResult {
  countryCode: string | undefined;
  nationalNumber: string;
}

export interface ParseToNationalAndCountryResult {
  country: string;
  nationalNumber: string;
}

/**
 * Servicio que encapsula parseo y formato de números de teléfono (E.164, AsYouType, etc.).
 * Responsabilidad única: transformar y validar cadenas de teléfono.
 */
@Injectable({ providedIn: 'root' })
export class PhoneFormatService {
  /**
   * Parsea una cadena con posible prefijo internacional (+ y dígitos) usando AsYouType
   * y resuelve el país cuando solo hay código de marcación (usando DIAL_CODE_DEFAULT_COUNTRY
   * o la primera coincidencia en la lista de países).
   */
  parseAsYouType(
    raw: string,
    countries: CountryPhone[],
  ): ParseAsYouTypeResult {
    const asYouType = new AsYouType();
    asYouType.input(raw);
    let countryCode: string | undefined = asYouType.getCountry() ?? undefined;
    const national = asYouType.getNationalNumber();
    const callingCode = asYouType.getCallingCode();

    if (!countryCode && callingCode && countries.length) {
      countryCode =
        DIAL_CODE_DEFAULT_COUNTRY[callingCode] ||
        countries.find((c) => c.dialCode === callingCode)?.iso2 ||
        undefined;
    }

    return {
      countryCode: countryCode ?? undefined,
      nationalNumber: national || raw,
    };
  }

  /**
   * Parsea un valor E.164 o en otro formato y devuelve código de país y número nacional.
   * Se usa para writeValue en el componente.
   */
  parseToNationalAndCountry(
    e164OrRaw: string,
    defaultCountryIso2: string,
  ): ParseToNationalAndCountryResult | null {
    const str = String(e164OrRaw).trim();
    if (!str) {
      return null;
    }
    const parsed = parsePhoneNumberFromString(str, defaultCountryIso2 as any);
    if (parsed) {
      return {
        country: parsed.country,
        nationalNumber: parsed.nationalNumber,
      };
    }
    const digitsOnly = str.replace(/\D/g, '');
    if (digitsOnly.length > 0) {
      return {
        country: defaultCountryIso2,
        nationalNumber: digitsOnly,
      };
    }
    return null;
  }

  /**
   * Construye el número E.164 y devuelve null si no es válido.
   */
  buildE164(dialCode: string, nationalDigits: string): string | null {
    const digits = (nationalDigits || '').replace(/\D/g, '');
    if (!digits.length) {
      return null;
    }
    const e164 = '+' + dialCode + digits;
    return isValidPhoneNumber(e164) ? e164 : null;
  }

  /**
   * Normaliza el input del usuario: solo dígitos y un único "+" al inicio.
   */
  normalizeRawInput(value: string): string {
    let raw = (value ?? '').replace(/[^\d+]/g, '');
    if (raw.indexOf('+') > 0) {
      raw = raw.replace(/\+/g, '');
    }
    if ((raw.match(/\+/g) || []).length > 1) {
      raw = '+' + raw.replace(/\+/g, '');
    }
    return raw;
  }
}